import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { DashboardService } from '../../../dashboard/dashboard.service';
import { AdminService } from '../../../admin/admin.service';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../../services/environment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BookManagementService } from '../../book-management.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-contract-summary',
  templateUrl: './contract-summary.component.html',
  styleUrls: ['./contract-summary.component.scss'],
  standalone: false,
})
export class ContractSummaryComponent implements OnInit, OnDestroy {
  private admnSrvc = inject(AdminService);
  private bmSvc = inject(BookManagementService);
  private cdRef = inject(ChangeDetectorRef);
  private dbs = inject(DashboardService);
  private envSvc = inject(EnvironmentService);
  private pltfrm = inject(Platform);
  private route = inject(Router);
  private snacky = inject(MatSnackBar);
  private ss = inject(SessionStorageService);

  @Input() reportName;
  @Input() headerType;
  private environment;
  private baseUrl: string;
  unsubscribe: Subject<any> = new Subject();
  selectedGroup = '';
  groupByItems = [];
  loading = true;
  fileLoading = false;
  skip = 0;
  take = 10;
  pageSizeOptions = [10, 25, 50, 75, 100];
  total = 0;
  gridData = { headers: [], data: [], total: 0 };
  filterValues = {};

  ngOnInit(): void {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    if (this.headerType === 'unit' || this.headerType === 'carrier') {
      this.groupByItems = [
        'Account Type',
        'Annuity Type',
        'Carrier',
        'Product',
      ];
      this.selectedGroup = 'Carrier';
    } else if (this.headerType === 'rep') {
      this.groupByItems = ['Annuity Type', 'Carrier', 'Client Name'];
      this.selectedGroup = 'Carrier';
    }

    this.bmSvc.updateBmWidgets$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(filterVals => {
        this.filterValues = filterVals;
        this.getUiConfig();
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUiConfig() {
    this.loading = true;
    const parsedSelectedGroup = this.selectedGroup
      .toLowerCase()
      .replace(/\s+/g, '-');

    this.admnSrvc
      .getUIConfigHeaders({
        uiconfig: 'pm-dashboard',
        type: `${this.headerType}-contract-summary-${parsedSelectedGroup}`,
        implementation: 'pm',
      })
      .subscribe(resp => {
        if (resp && resp['headers']) {
          this.gridData.headers = resp['headers'];
        }
        this.getContractSummary();
      });
  }

  getContractSummary() {
    const reportParams = {
      take: this.take,
      skip: this.skip,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      group: this.selectedGroup,
      name: this.reportName,
    };
    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
      'userId',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReport(reportParams).subscribe({
      next: resp => {
        if (resp && resp['data']) {
          this.gridData.data = resp['data'];
          this.gridData.total = resp['total'];
        }
        this.loading = false;
        this.cdRef.detectChanges();
      },
      error: err => {
        console.warn(err);
        this.loading = false;
      },
    });
  }

  dataStateChange(ev) {
    this.loading = true;
    this.take = ev.take;
    this.skip = ev.skip;
    this.getContractSummary();
  }

  downloadData() {
    this.fileLoading = true;
    const reportParams = {
      take: 10000,
      skip: 0,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      group: this.selectedGroup,
      name: this.reportName,
    };
    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReportXlsx(reportParams).subscribe({
      next: data => {
        if (data) {
          const date = new Date();
          const filename =
            'ContractSummary-' +
            (date.getMonth() + 1) +
            date.getDate() +
            date.getFullYear() +
            '.xlsx';
          const iosSafari =
            this.pltfrm.IOS ||
            (navigator?.['userAgentData']?.platform === 'macOS' &&
              navigator.maxTouchPoints > 1);

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
            this.fileLoading = false;
            this.snacky.open(`Download complete.`, 'Close', {
              duration: 4000,
            });
          };
        } else {
          this.fileLoading = false;
        }
      },
      error: () => {
        this.snacky.open(
          `Oops! Something went wrong. Please try again.`,
          'Close',
          {
            duration: 4000,
          }
        );
        this.fileLoading = false;
      },
    });
  }

  launchRecord(dataItem) {
    let gridName = 'BookManagementFilters';
    const filterObj = {
      logic: 'and',
      filters: [],
    };
    let navRoute = 'book-management';
    if (this.reportName.includes('PM Carrier')) {
      gridName = 'CarrierPolicyDetailsFilters';
      navRoute = 'carrier-policy-details';
    }

    if (this.filterValues['start'] && this.filterValues['end']) {
      filterObj['filters'].push(
        {
          field: 'IssueDate',
          operator: 'start',
          value: this.filterValues['start'],
        },
        {
          field: 'IssueDate',
          operator: 'end',
          value: this.filterValues['end'],
        }
      );
    }

    if (this.filterValues['unitId']) {
      filterObj['filters'].push({
        field: 'unitID',
        operator: 'selected',
        value: [this.filterValues['unitId']],
      });
    }

    if (this.filterValues['npn']) {
      filterObj['filters'].push({
        field: 'NIPR',
        operator: 'selected',
        value: this.filterValues['npn'],
      });
    }

    switch (this.selectedGroup) {
      case 'Account Type':
        filterObj['filters'].push({
          field: 'IRSCode',
          operator: 'selected',
          value: [dataItem.irsCode],
        });
        break;
      case 'Annuity Type':
        filterObj['filters'].push({
          field: 'ProductType',
          operator: 'selected',
          value: [dataItem.productType],
        });
        break;
      case 'Carrier':
        filterObj['filters'].push({
          field: 'Carrier',
          operator: 'selected',
          value: [dataItem.carrier],
        });
        break;
      case 'Product':
        filterObj['filters'].push({
          field: 'Name',
          operator: 'contains',
          value: [dataItem.name],
        });
        break;
      case 'Client Name':
        filterObj['filters'].push({
          field: 'PartyName',
          operator: 'searchString',
          value: dataItem.partyName,
        });
        break;
    }

    const selectionVars = [
      { group: 'Account Type', varName: 'accountType', dataMap: 'IRSCode' },
      { group: 'Annuity Type', varName: 'productType', dataMap: 'ProductType' },
      { group: 'Carrier', varName: 'carrier', dataMap: 'Carrier' },
      { group: 'Product', varName: 'productName', dataMap: 'Name' },
    ];

    selectionVars.map(selectionVar => {
      if (
        this.filterValues[selectionVar.varName] &&
        this.filterValues[selectionVar.varName].length > 0 &&
        !filterObj['filters'].find(obj => obj.field === 'productName') &&
        this.selectedGroup !== selectionVar.group
      ) {
        filterObj['filters'].push({
          field: selectionVar.dataMap,
          operator: 'selected',
          value: this.filterValues[selectionVar.varName],
        });
      }
    });
    if (
      this.filterValues['userId'] &&
      this.filterValues['userId'].filter(userIdItem => userIdItem).length &&
      (this.headerType === 'unit' || this.headerType === 'carrier')
    ) {
      filterObj['filters'].push({
        field: 'userId',
        operator: 'selected',
        value: this.filterValues['userId'],
      });
    } else if (this.headerType === 'rep') {
      filterObj['filters'].push({
        field: 'userId',
        operator: 'selected',
        value: [this.ss.get('globals').user.id],
      });
    }

    this.ss.set(gridName, filterObj);
    this.route.navigate([`/${navRoute}`]);
  }
}
